import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logoImg from "../../assets/images/talktokam.webp";
import { BRAND } from "../../theme/talktokam";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const headerBg = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["rgba(5,8,22,0.65)", "rgba(255,255,255,0.95)"],
  );
  const headerShadow = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["0 0 0 rgba(0,0,0,0)", "0 4px 30px rgba(0,0,0,0.08)"],
  );
  const homeLinkColor = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["#ffffff", "#111827"],
  );
  const navLinkColor = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["#ffffff", "#4b5563"],
  );
  const loginColor = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["#ffffff", "#374151"],
  );
  const hamburgerBg = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["rgba(255,255,255,0.06)", "#050816"],
  );
  const hamburgerBorder = useTransform(
    scrollYProgress,
    [0, 0.08],
    ["1px solid rgba(255,255,255,0.1)", "1px solid rgba(255,255,255,0.15)"],
  );

  const navItems = [
    { label: "Home", href: "/" },
    { label: "Features", href: "/features" },
    { label: "Pricing", href: "/pricing" },
    { label: "Docs", href: "/docs" },
    { label: "About", href: "/about" },
    { label: "Contact us", href: "/contact" },
  ];

  const drawerContent = (
    <Box sx={{ width: 260, pt: 2, bgcolor: "#050816", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
        <IconButton
          onClick={() => setMobileOpen(false)}
          sx={{ color: "#94a3b8" }}
        >
          <X size={20} />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.href);
                setMobileOpen(false);
              }}
              sx={{
                py: 1.5,
                px: 3,
                color: location.pathname === item.href ? "#38bdf8" : "#cbd5e1",
                fontWeight: location.pathname === item.href ? 700 : 500,
                "&:hover": {
                  color: "#38bdf8",
                  bgcolor: "rgba(56,189,248,0.06)",
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                  fontWeight: "inherit",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <motion.header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        background: headerBg,
        boxShadow: headerShadow,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        overflow: "hidden",
        transition: "background 0.3s",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 70,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <Box
                component="img"
                src={logoImg}
                alt="Talktokam"
                sx={{ height: 56, width: "auto", borderRadius: "8px" }}
              />
            </Box>
          </motion.div>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 4,
            }}
          >
            {navItems.map((item, i) => (
              <Box
                key={item.label}
                component={motion.div}
                style={{
                  color:
                    location.pathname === item.href
                      ? homeLinkColor
                      : navLinkColor,
                }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i, duration: 0.4 }}
                onClick={() => navigate(item.href)}
                sx={{
                  cursor: "pointer",
                  fontWeight: location.pathname === item.href ? 700 : 500,
                  "&:hover": { color: BRAND.cyan },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ color: loginColor }}
            >
              <Button
                onClick={() => navigate("/login")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "rgba(148,163,184,0.1)" },
                }}
              >
                Log in
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button
                variant="contained"
                onClick={() => navigate("/signup")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                  boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                  "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.5)" },
                }}
              >
                Get Started Free
              </Button>
            </motion.div>
            <motion.div
              style={{ background: hamburgerBg, border: hamburgerBorder, borderRadius: "50%", display: "inline-flex" }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  width: 36,
                  height: 36,
                  color: "#ffffff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                }}
              >
                <Menu size={20} />
              </IconButton>
            </motion.div>
          </Box>
        </Box>
      </Container>
      {mobileOpen && (
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              bgcolor: "#050816",
              borderLeft: "1px solid rgba(255,255,255,0.06)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </motion.header>
  );
};

export default Header;
